import type { NotamItem, NotamType } from '@/types';

// ---------------------------------------------------------------------------
// NOTAM — Notice to Air Missions
// Source: Aviation Weather NOTAM API (free, no auth required)
// https://api.aviationweather.gov/api/data/notam
//
// Queries key military/conflict airport ICAO codes.
// In dev: calls /api/notam (Vite middleware, 1-hour cache).
// Parses raw NOTAM text to extract coordinates, altitude limits, category.
// ---------------------------------------------------------------------------

// ICAO airport coordinates for lat/lon assignment when NOTAM has no explicit coords
const ICAO_COORDS: Record<string, [number, number]> = {
  // Iran
  OIII: [35.69, 51.31], OIMM: [36.24, 59.64], OIKB: [27.22, 56.37], OISS: [29.54, 52.59],
  // Gulf / Saudi / Iraq
  OKBK: [29.23, 47.97], OMAA: [24.44, 54.65], OEJN: [21.68, 39.16], OERK: [24.96, 46.70],
  ORBI: [33.26, 44.23], ORSU: [35.56, 45.32],
  // Yemen
  OYYE: [15.47, 44.22], OYSN: [12.83, 45.03],
  // Korea
  RKSS: [37.56, 126.79], RKSI: [37.46, 126.44], RKNY: [37.95, 128.67],
  RKPK: [35.18, 128.94], RKTL: [36.72, 127.50],
  // Japan (US bases)
  RJOI: [34.14, 132.23], RJTY: [35.75, 139.35], RJFK: [26.35, 127.77],
  // Taiwan
  RCTP: [25.08, 121.23], RCSS: [25.07, 121.55],
  // Ukraine
  UKBB: [50.34, 30.89], UKLR: [49.81, 23.95], UKOO: [46.43, 30.68],
};

interface RawNotam {
  notamID?: string;
  location?: string;
  notamType?: string;
  icaoMessage?: string;
  traditionalMessage?: string;
  startDate?: string;
  endDate?: string;
  coordinates?: { lat?: number; lon?: number };
  radius?: number;
  minimumAlt?: { value?: number; unitCode?: string };
  maximumAlt?: { value?: number; unitCode?: string };
  [key: string]: unknown;
}

function classifyNotam(text: string): NotamItem['category'] {
  const t = text.toUpperCase();
  if (/\b(RESTRICTED|PROHIBIT|DANGER|ADIZ)\b/.test(t)) return 'restricted';
  if (/\b(MIL|MILITARY|EXERCISE|TRAINING|FIRING|WEAPONS)\b/.test(t)) return 'military';
  if (/\b(TFR|TEMPORARY FLIGHT RESTRICTION|SPECIAL USE)\b/.test(t)) return 'tfrzone';
  if (/\b(AIRSHOW|AEROBATIC|FORMATION)\b/.test(t)) return 'airshow';
  return 'other';
}

function parseNotamType(raw: string | undefined): NotamType {
  if (!raw) return 'D';
  const t = raw.toUpperCase();
  if (t.includes('FDC')) return 'FDC';
  if (t.includes('MIL')) return 'MIL';
  if (t.includes('INTL')) return 'INTL';
  return 'D';
}

function parseNotam(raw: RawNotam): NotamItem | null {
  const id = raw.notamID ?? String(Math.random());
  const location = raw.location ?? '';
  const text = raw.icaoMessage ?? raw.traditionalMessage ?? '';
  if (!text) return null;

  // Try coordinates from API response first, then from ICAO lookup
  let lat = raw.coordinates?.lat ?? null;
  let lon = raw.coordinates?.lon ?? null;

  if (lat == null || lon == null) {
    const coords = ICAO_COORDS[location.toUpperCase()];
    if (!coords) return null; // cannot place on map
    [lat, lon] = coords;
  }

  const radius = raw.radius ?? 5; // default 5nm radius

  const altMin = raw.minimumAlt?.value != null
    ? `${raw.minimumAlt.value}${raw.minimumAlt.unitCode ?? 'FT'}`
    : undefined;
  const altMax = raw.maximumAlt?.value != null
    ? `${raw.maximumAlt.value}${raw.maximumAlt.unitCode ?? 'FT'}`
    : undefined;

  const now = new Date();
  const endDate = raw.endDate;
  const isActive = !endDate || new Date(endDate) > now;

  if (!isActive) return null; // skip expired NOTAMs

  return {
    id,
    location,
    type: parseNotamType(raw.notamType),
    text: text.slice(0, 600),
    lat,
    lon,
    radiusNm: typeof radius === 'number' ? radius : 5,
    altitudeMin: altMin,
    altitudeMax: altMax,
    startDate: raw.startDate ?? now.toISOString(),
    endDate: raw.endDate,
    isActive,
    category: classifyNotam(text),
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

let _cache: NotamItem[] | null = null;
let _cacheTs = 0;
const CLIENT_CACHE_TTL = 30 * 60_000; // 30 min

/**
 * Fetch active NOTAMs for military/conflict monitoring locations.
 * In dev: calls /api/notam (Vite middleware, 1h cache).
 * Falls back to empty array on failure.
 */
export async function fetchNotams(): Promise<NotamItem[]> {
  const now = Date.now();
  if (_cache && now - _cacheTs < CLIENT_CACHE_TTL) return _cache;

  try {
    const res = await fetch('/api/notam', { signal: AbortSignal.timeout(25_000) });
    if (res.ok) {
      const json = await res.json() as { notams: RawNotam[]; count: number; error?: string };
      if (json.error) throw new Error(json.error);
      const items = (json.notams ?? [])
        .map(parseNotam)
        .filter((n): n is NotamItem => n !== null);
      _cache = items;
      _cacheTs = now;
      console.log(`[NOTAM] ${items.length} active NOTAMs loaded`);
      return items;
    }
  } catch (err) {
    console.warn('[NOTAM] fetch failed:', err);
  }

  _cache = [];
  _cacheTs = now;
  return [];
}

export const NOTAM_CATEGORY_COLORS: Record<NotamItem['category'], [number, number, number, number]> = {
  restricted: [239, 68,  68,  200], // red
  military:   [167, 139, 250, 200], // purple
  tfrzone:    [251, 191, 36,  200], // yellow
  airshow:    [52,  211, 153, 180], // green
  other:      [148, 163, 184, 150], // slate
};
